import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CheckSquare, Square, RefreshCw, Image as ImageIcon, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { getImageUrl, CATEGORY_LABELS } from '../../config';
import { useAuth } from '../../context/AuthContext';

const CATEGORY_FILTERS = ['all', 'ecran', 'pc', 'accessoire'];
const STATE_FILTERS = [
  { key: 'all', label: 'Toutes' },
  { key: 'suspect', label: 'Photos suspectes (auto)' },
  { key: 'no_review', label: 'Pas encore révisées' },
  { key: 'placeholder', label: 'Placeholder appliqué' },
  { key: 'reviewed_ok', label: 'Validées OK' },
];

/**
 * Page admin /my/photo-audit : revue visuelle rapide de toutes les photos
 * produit avec actions en 1 clic (remplacer par placeholder catégorie /
 * marquer comme OK). Résout les "photos farfelues" issues du pipeline
 * catalog_import (chantier #9, 2026-06-01).
 */
export default function PhotoAudit() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(null); // null | 'load' | 'batch' | `apply:${pid}` | `review:${pid}`

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`/api/admin/photo-audit`, { credentials: 'include' });
      const data = await resp.json();
      if (data?.success && data?.data) {
        setItems(data.data.items || []);
        setCounts(data.data.counts || {});
      } else {
        console.warn('Photo audit load failed', data);
      }
    } catch (e) {
      console.error('Photo audit error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (filterCat !== 'all' && it.category !== filterCat) return false;
      if (filterState === 'suspect' && !it.analysis?.any_suspect) return false;
      if (filterState === 'no_review' && (it.photo_reviewed_at || it.photo_is_placeholder)) return false;
      if (filterState === 'placeholder' && !it.photo_is_placeholder) return false;
      if (filterState === 'reviewed_ok' && !it.photo_review_ok) return false;
      return true;
    });
  }, [items, filterCat, filterState]);

  const toggleSelect = (pid) => {
    const next = new Set(selectedIds);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(i => i.product_id)));
  };

  const applyPlaceholderTo = async (pid) => {
    setBusy(`apply:${pid}`);
    try {
      const resp = await fetch(`/api/admin/photo-audit/${pid}/apply`, {
        method: 'POST', credentials: 'include',
      });
      const data = await resp.json();
      if (data?.success) await load();
      else alert('Échec : ' + (data?.message || 'erreur'));
    } catch (e) {
      alert('Erreur réseau : ' + e.message);
    } finally {
      setBusy(null);
    }
  };

  const markReviewed = async (pid, photoOk) => {
    setBusy(`review:${pid}`);
    try {
      const resp = await fetch(`/api/admin/photo-audit/${pid}/mark-reviewed?photo_ok=${photoOk}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await resp.json();
      if (data?.success) await load();
    } catch (e) {
      alert('Erreur réseau : ' + e.message);
    } finally {
      setBusy(null);
    }
  };

  const batchApply = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Remplacer les photos de ${selectedIds.size} produit(s) par le placeholder de leur catégorie ?`)) return;
    setBusy('batch');
    try {
      const resp = await fetch(`/api/admin/photo-audit/batch-apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: [...selectedIds] }),
      });
      const data = await resp.json();
      if (data?.success) {
        setSelectedIds(new Set());
        await load();
      } else {
        alert('Échec batch : ' + (data?.message || 'erreur'));
      }
    } catch (e) {
      alert('Erreur réseau : ' + e.message);
    } finally {
      setBusy(null);
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-slate-600">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Chargement…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="px-6 py-5">
        <div className="flex items-baseline justify-between mb-2">
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Audit photos produits</h1>
          <button onClick={load} className="text-sm text-secondary hover:underline flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Recharger
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          Revue visuelle rapide des photos importées. Clique sur "Remplacer par placeholder" pour les photos farfelues — un placeholder propre par catégorie sera utilisé.
        </p>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Kpi label="Total" value={counts.total || 0} />
          <Kpi label="Sans photo" value={counts.no_photo || 0} color="amber" />
          <Kpi label="Suspectes (auto)" value={counts.suspect || 0} color="red" />
          <Kpi label="Placeholder actif" value={counts.placeholder_used || 0} color="blue" />
          <Kpi label="Validées OK" value={counts.reviewed_ok || 0} color="emerald" />
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-xs uppercase tracking-wider text-slate-500 self-center mr-2">Catégorie :</span>
          {CATEGORY_FILTERS.map(c => (
            <button key={c} onClick={() => setFilterCat(c)}
                    className={`px-3 py-1 text-xs rounded-full ${filterCat === c ? 'bg-secondary text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              {c === 'all' ? 'Toutes' : (CATEGORY_LABELS?.[c] || c)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="text-xs uppercase tracking-wider text-slate-500 self-center mr-2">État :</span>
          {STATE_FILTERS.map(s => (
            <button key={s.key} onClick={() => setFilterState(s.key)}
                    className={`px-3 py-1 text-xs rounded-full ${filterState === s.key ? 'bg-secondary text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Barre actions batch */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between bg-secondary/10 border border-secondary/30 rounded-lg px-4 py-3 mb-4">
            <span className="text-sm font-medium text-slate-900">
              {selectedIds.size} produit(s) sélectionné(s)
            </span>
            <div className="flex gap-2">
              <button onClick={batchApply} disabled={busy === 'batch'}
                      className="px-4 py-2 text-sm bg-secondary text-white rounded font-medium hover:bg-primary disabled:opacity-50 flex items-center gap-2">
                {busy === 'batch' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                Remplacer par placeholder
              </button>
              <button onClick={() => setSelectedIds(new Set())}
                      className="px-3 py-2 text-sm bg-slate-200 text-slate-700 rounded hover:bg-slate-300">
                ✕ Désélectionner
              </button>
            </div>
          </div>
        )}

        {/* Compteur résultats + select all */}
        <div className="flex items-center justify-between mb-4 text-sm text-slate-600">
          <button onClick={toggleAll} className="flex items-center gap-2 hover:text-slate-900">
            {selectedIds.size === filtered.length && filtered.length > 0
              ? <CheckSquare className="w-4 h-4 text-secondary" />
              : <Square className="w-4 h-4" />}
            Tout {selectedIds.size === filtered.length ? 'désélectionner' : 'sélectionner'} ({filtered.length})
          </button>
        </div>

        {/* Grille produits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map(item => (
            <ProductPhotoCard
              key={item.product_id}
              item={item}
              selected={selectedIds.has(item.product_id)}
              onSelect={() => toggleSelect(item.product_id)}
              onApplyPlaceholder={() => applyPlaceholderTo(item.product_id)}
              onMarkOk={() => markReviewed(item.product_id, true)}
              busyApply={busy === `apply:${item.product_id}`}
              busyReview={busy === `review:${item.product_id}`}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500 italic">
            Aucun produit ne correspond aux filtres.
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Kpi({ label, value, color = 'slate' }) {
  const cls = {
    slate: 'bg-slate-50 text-slate-900',
    red: 'bg-red-50 text-red-700',
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
  }[color] || 'bg-slate-50 text-slate-900';
  return (
    <div className={`rounded-lg px-3 py-3 ${cls}`}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-heading font-bold">{value}</div>
    </div>
  );
}

function ProductPhotoCard({ item, selected, onSelect, onApplyPlaceholder, onMarkOk, busyApply, busyReview }) {
  const img = item.images?.[0];
  const imgUrl = img ? getImageUrl(img) : null;
  const isSuspect = item.analysis?.any_suspect;
  const score = item.analysis?.worst_quality_score ?? 100;

  return (
    <div className={`bg-white border rounded-lg overflow-hidden flex flex-col ${selected ? 'border-secondary ring-2 ring-secondary/30' : 'border-slate-200'}`}>
      {/* Image preview + checkbox */}
      <div className="relative aspect-[705/450] bg-slate-100 cursor-pointer" onClick={onSelect}>
        {imgUrl ? (
          <img src={imgUrl} alt="" className="w-full h-full object-contain" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">Pas de photo</div>
        )}
        <div className="absolute top-2 left-2">
          {selected
            ? <CheckSquare className="w-5 h-5 text-secondary bg-white rounded" />
            : <Square className="w-5 h-5 text-slate-400 bg-white/80 rounded" />}
        </div>
        {/* Badges état */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
          {item.photo_is_placeholder && (
            <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">Placeholder</span>
          )}
          {item.photo_review_ok && (
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">✓ Validé</span>
          )}
          {isSuspect && !item.photo_is_placeholder && !item.photo_review_ok && (
            <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Suspect ({score.toFixed(0)})
            </span>
          )}
        </div>
      </div>

      {/* Infos + actions */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="flex-1">
          <div className="text-xs text-slate-500 uppercase tracking-wider flex justify-between">
            <span>{item.category}</span>
            <span>{item.condition}</span>
          </div>
          <div className="text-sm font-medium text-slate-900 line-clamp-2 mt-1">{item.name}</div>
          <div className="text-xs text-slate-500 mt-1">{item.brand}</div>
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <button onClick={onApplyPlaceholder} disabled={busyApply}
                  className="flex-1 px-2 py-1.5 text-xs bg-slate-100 hover:bg-secondary hover:text-white rounded font-medium disabled:opacity-50">
            {busyApply ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : 'Remplacer'}
          </button>
          <button onClick={onMarkOk} disabled={busyReview}
                  className="flex-1 px-2 py-1.5 text-xs bg-slate-100 hover:bg-emerald-500 hover:text-white rounded font-medium disabled:opacity-50">
            {busyReview ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : '✓ OK'}
          </button>
        </div>
      </div>
    </div>
  );
}
